import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  LinearProgress,
  Tooltip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Popper,
  ClickAwayListener,
  Avatar,
  Checkbox,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  AutoFixHigh as AutoFixHighIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  TextFields as TextIcon,
  DataObject as ObjectIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  FormatListBulleted as ListIcon,
  Extension as PluginIcon,
  Mic as AudioIcon,
  CallSplit as SplitIcon,
  PhotoLibrary as CarouselIcon,
  MovieCreation as MovieIcon,
  Article as BlogIcon,
  Description as DocIcon,
  RocketLaunch as LaunchIcon,
  QueryStats as ResearchIcon,
  Email as EmailIcon,
  Autorenew as RepurposeIcon,
  Groups as MeetingIcon,
  Slideshow as ReelIcon,
  Campaign as AdIcon,
  LinkedIn as LinkedInIcon,
  Tag as TwitterIcon,
  YouTube as YouTubeIcon,
  PushPin as PinterestIcon,
  ViewCarousel as StoryIcon,
  Dashboard as KitIcon,
  OndemandVideo as ShortsIcon,
  TrendingUp as SEOIcon,
  Tune as TuneIcon,
  Palette as PaletteIcon,
  ChevronLeft as ChevronLeftIcon,
  Newspaper as NewspaperIcon,
  Storefront as StorefrontIcon,
  Event as EventIcon,
  WorkspacePremium as CaseStudyIcon,
  MonetizationOn as MonetizeIcon,
  ColorLens as StyleExtractIcon,
  ContentCut as ClipperIcon,
  Speed as SpeedIcon,
  ShoppingCart as ShoppingCartIcon,
  FamilyRestroom as FamilyIcon,
  FitnessCenter as FitnessIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_ALLOWED_MODELS, getTextModels, getImageGenerationModels, getVideoModels, getModelCapabilitySummary, ModelOption } from '../config/modelConfig';
import { ModelSelector } from './ModelSelector';
import { isLlmBatchPlugin } from './PluginUsageInfo';
import { TemperatureSlider } from './TemperatureSlider';
import { StepInputFields } from './StepInputFields';
import { pluginInputs, BuilderPlugin } from './chainBuilderCatalog';
import { BatchJobSettings } from './BatchJobSettings';
import { BuilderPalette, builderMentionItems } from './BuilderPalette';
import { StepReliability } from './reliability/StepReliability';

const StepSection: React.FC<{ title: string; hint?: string; first?: boolean }> = ({ title, hint, first }) => (
  <Grid item xs={12} sx={{ pb: 0, '&.MuiGrid-item': { pt: first ? 2 : 2.5 } }}>
    {!first && <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />}
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.5 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {hint}
      </Typography>
    )}
  </Grid>
);

const PALETTE_OPEN_KEY = 'easyai.chainBuilder.paletteOpen';

function readPaletteOpen(): boolean {
  try {
    const stored = localStorage.getItem(PALETTE_OPEN_KEY);
    if (stored === '0' || stored === 'false') return false;
  } catch { /* localStorage unavailable */ }
  return true;
}

function writePaletteOpen(open: boolean) {
  try {
    localStorage.setItem(PALETTE_OPEN_KEY, open ? '1' : '0');
  } catch { /* localStorage unavailable */ }
}

// ── Types ───────────────────────────────────────────────────────────────────

interface ReturnField {
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export interface ChainStep {
  step_number: number;
  name: string;
  display_name: string;
  description: string;
  step_type: 'ai_function' | 'existing_function' | 'plugin';
  existing_id: string | null;
  instructions: string | null;
  input_types: string[];
  allowed_models: string[];
  response_type: string;
  return_object: { name: string; fields: ReturnField[] } | null;
  map_over_array: boolean;
  array_field: string | null;
  skip_item_when_empty: string | null;
  input_template: Record<string, any> | null;
  enable_google_search?: boolean;
  /** Per-node model-call timeout override (seconds, 10-180). Blank = global default. */
  timeout_seconds?: number | null;
  /** Sampling temperature 0-2. Unset = model default (Gemini/GPT-4 = 0). GPT-5 is locked at 1. */
  temperature?: number | null;
  optional?: boolean;
  /** Knowledge Hub category IDs searched before this step runs (RAG). */
  knowledge_categories?: string[];
  /** Snippets to retrieve (1-10). Unset = 3. */
  knowledge_chunks?: number | null;
  /** Search text; supports {{input.x}} and {{step-N.x}}. Blank = this step's input. */
  knowledge_query?: string | null;
}

export interface RuntimeInput {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'url' | 'number' | 'boolean' | 'select' | 'file' | 'upload';
  required: boolean;
  placeholder?: string;
  default?: any;
  accept?: string;
  allow_file?: boolean;
  paste?: boolean;
  max_files?: number;
  max_size_mb?: number;
  file_key?: string;
  text_key?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface ChainPlan {
  chain_name: string;
  chain_description: string;
  steps: ChainStep[];
  edges: Array<{ id?: string; source: string; target: string }>;
  runtime_inputs?: RuntimeInput[];
}

interface ChainWizardProps {
  open: boolean;
  onClose: () => void;
  onChainCreated: (chainId: string) => void;
  /** If provided, opens in "edit" mode with this prompt pre-filled, waiting for user changes before regenerating. */
  initialPrompt?: string;
  /** Slug of the template that originally generated this chain (for re-matching the form). */
  templateSlug?: string;
  /** Existing chain ID for re-saving (update instead of create). */
  existingChainId?: string;
  /** Existing chain name pre-fill. */
  existingChainName?: string;
  /** Existing chain description pre-fill. */
  existingChainDescription?: string;
  /** If provided, opens directly in review phase with this plan loaded (skip generation). */
  existingPlan?: ChainPlan;
  /** Pre-fills the description for a new chain (create mode, unlike `initialPrompt`). */
  seedPrompt?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const RESPONSE_TYPE_INFO: Record<string, { icon: React.ReactNode; label: string; color: string; hint: string }> = {
  text:   { icon: <TextIcon fontSize="small" />,   label: 'Text',   color: '#1976d2', hint: 'Returns plain text' },
  object: { icon: <ObjectIcon fontSize="small" />, label: 'Object', color: '#388e3c', hint: 'Returns structured JSON object' },
  list:   { icon: <ListIcon fontSize="small" />,   label: 'List',   color: '#7b1fa2', hint: 'Returns an array of objects' },
  image:  { icon: <ImageIcon fontSize="small" />,  label: 'Image',  color: '#e65100', hint: 'Generates image(s) from text prompt' },
  video:  { icon: <VideoIcon fontSize="small" />,  label: 'Video',  color: '#c62828', hint: 'Generates video from text prompt' },
  audio:  { icon: <AudioIcon fontSize="small" />,  label: 'Audio',  color: '#00695c', hint: 'Audio transcription' },
};

// All available models — text + image + video
const WIZARD_MODEL_OPTIONS: ModelOption[] = (() => {
  const text = getTextModels();
  const image = getImageGenerationModels();
  const video = getVideoModels();
  return [...text, ...image, ...video].map(m => ({
    value: m.apiName,
    label: m.displayName,
    provider: m.provider,
    capabilityLabel: getModelCapabilitySummary(m),
    category: m.category,
    costRating: m.costRating,
    speedRating: m.speedRating,
    intelligenceRating: m.intelligenceRating,
  }));
})();

const PHASE_LABELS = ['Describe', 'Review & Edit', 'Save'];

// ── Component ───────────────────────────────────────────────────────────────

interface PromptTemplate {
  slug: string;
  name: string;
  description: string;
  prompt: string;
}

const TEMPLATE_CATEGORIES: { label: string; slugs: string[] }[] = [
  {
    label: 'Meal Planning',
    slugs: ['meal_plan_fast', 'meal_plan_macro_tuned', 'meal_plan_budget', 'meal_plan_family', 'meal_plan_athletic'],
  },
  {
    label: 'Social Posts',
    slugs: ['instagram_carousel', 'facebook_ad_image', 'linkedin_post', 'x_twitter_thread', 'pinterest_pin', 'youtube_thumbnail'],
  },
  {
    label: 'Campaigns & Ads',
    slugs: ['social_media_kit', 'paid_ad_suite', 'seo_ad_optimizer', 'product_launch_kit', 'event_webinar_kit'],
  },
  {
    label: 'Content & Copy',
    slugs: ['blog_post_seo', 'ecommerce_product_copy', 'press_release_pr_kit', 'email_sequence', 'content_repurposer', 'case_study_generator', 'brand_style_guide'],
  },
  {
    label: 'Video',
    slugs: ['social_video_reel', 'story_to_video', 'tiktok_reels_video', 'youtube_shorts_video', 'ig_story_video', 'video_clipper'],
  },
  {
    label: 'Research & Docs',
    slugs: ['competitor_research', 'website_style_extractor', 'document_analyzer', 'meeting_notes_actions'],
  },
];

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  instagram_carousel: <CarouselIcon />,
  story_to_video: <MovieIcon />,
  social_video_reel: <ReelIcon />,
  blog_post_seo: <BlogIcon />,
  document_analyzer: <DocIcon />,
  product_launch_kit: <LaunchIcon />,
  competitor_research: <ResearchIcon />,
  email_sequence: <EmailIcon />,
  content_repurposer: <RepurposeIcon />,
  meeting_notes_actions: <MeetingIcon />,
  tiktok_reels_video: <ReelIcon />,
  facebook_ad_image: <AdIcon />,
  linkedin_post: <LinkedInIcon />,
  x_twitter_thread: <TwitterIcon />,
  youtube_thumbnail: <YouTubeIcon />,
  pinterest_pin: <PinterestIcon />,
  ig_story_video: <StoryIcon />,
  social_media_kit: <KitIcon />,
  youtube_shorts_video: <ShortsIcon />,
  seo_ad_optimizer: <SEOIcon />,
  brand_style_guide: <PaletteIcon />,
  press_release_pr_kit: <NewspaperIcon />,
  ecommerce_product_copy: <StorefrontIcon />,
  event_webinar_kit: <EventIcon />,
  case_study_generator: <CaseStudyIcon />,
  paid_ad_suite: <MonetizeIcon />,
  website_style_extractor: <StyleExtractIcon />,
  video_clipper: <ClipperIcon />,
  meal_plan_fast: <SpeedIcon />,
  meal_plan_macro_tuned: <TuneIcon />,
  meal_plan_budget: <ShoppingCartIcon />,
  meal_plan_family: <FamilyIcon />,
  meal_plan_athletic: <FitnessIcon />,
};

interface TemplateField {
  label: string;
  key: string;
  storageKey: string;
  placeholder: string;
  multiline: boolean;
  optional: boolean;
}

const FIELD_STORAGE_PREFIX = 'chain_tpl_field_';

const CANONICAL_KEY_MAP: Record<string, string> = {
  brand_account_name: 'brand_name',
  brand_company_name: 'brand_name',
  brand_business_name: 'brand_name',
  brand_account: 'brand_name',
  author_company_name: 'brand_name',
  account_author_name: 'brand_name',
  channel_name: 'channel_name',
  instagram_url: 'instagram_url',
  style_guide: 'style_guide',
  style_guide_brand_voice: 'style_guide',
  brand_style_guide: 'style_guide',
  call_to_action: 'call_to_action',
  cta_swipe_up: 'call_to_action',
  end_screen_cta: 'call_to_action',
  tone: 'tone',
  tone_style: 'tone',
  tone_mood: 'tone',
  target_audience: 'target_audience',
  image_style_notes: 'image_style_notes',
  music_audio_notes: 'music_notes',
  target_duration: 'target_duration',
  target_platform: 'target_platform',
  enable_web_research: 'enable_web_research',
};

function canonicalStorageKey(fieldKey: string): string {
  return FIELD_STORAGE_PREFIX + (CANONICAL_KEY_MAP[fieldKey] || fieldKey);
}

function loadFieldDefaults(fields: TemplateField[]): Record<string, string> {
  const vals: Record<string, string> = {};
  for (const f of fields) {
    try {
      const stored = localStorage.getItem(f.storageKey);
      if (stored) vals[f.key] = stored;
    } catch { /* localStorage unavailable */ }
  }
  return vals;
}

function saveFieldValue(storageKey: string, value: string) {
  try {
    if (value.trim()) {
      localStorage.setItem(storageKey, value);
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch { /* localStorage unavailable */ }
}

function parseTemplateFields(promptText: string): { fields: TemplateField[]; beforeConfig: string; afterConfig: string; instructions: string } | null {
  const sepRegex = /═{3,}/g;
  const matches = Array.from(promptText.matchAll(sepRegex));
  if (matches.length < 4) return null;

  const configStartSep = matches[1];
  const configEndSep = matches[2];
  const configStart = (configStartSep.index ?? 0) + configStartSep[0].length;
  const configEnd = configEndSep.index ?? promptText.length;

  const headerBlock = promptText.slice(configStart, configEnd);
  const lines = headerBlock.split('\n');

  const beforeConfig = promptText.slice(0, configStartSep.index ?? 0);
  const afterConfig = promptText.slice(configEnd + (configEndSep[0].length));

  const instrSep = matches[3];
  const instructions = promptText.slice((instrSep.index ?? 0) + instrSep[0].length);

  const fields: TemplateField[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const labelMatch = line.match(/^([A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?)\s*:\s*$/);
    if (labelMatch) {
      const rawLabel = labelMatch[1].trim();
      const optional = /\(optional\)/i.test(rawLabel);
      const label = rawLabel.replace(/\s*\(optional\)\s*/i, '').trim();
      const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const descLines: string[] = [];
      i++;
      while (i < lines.length) {
        const dl = lines[i];
        if (dl.match(/^[A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?\s*:\s*$/) || dl.match(/═{3,}/)) break;
        if (dl.trim()) descLines.push(dl.trim());
        i++;
      }
      let desc = descLines.join(' ').replace(/^\[/, '').replace(/\]$/, '').trim();
      if (!desc) desc = label;
      const multiline = desc.length > 120 || /paste|content|style guide|description/i.test(label);
      const storageKey = canonicalStorageKey(key);
      fields.push({ label, key, storageKey, placeholder: desc, multiline, optional });
    } else {
      i++;
    }
  }
  return fields.length > 0 ? { fields, beforeConfig, afterConfig, instructions } : null;
}

function rebuildPromptFromFields(template: PromptTemplate, fieldValues: Record<string, string>, instructionsOverride?: string): string {
  const text = template.prompt;
  const sepRegex = /═{3,}/g;
  const matches = Array.from(text.matchAll(sepRegex));
  if (matches.length < 4) return text;

  const configStartSep = matches[1];
  const configEndSep = matches[2];
  const configStart = (configStartSep.index ?? 0) + configStartSep[0].length;
  const configEnd = configEndSep.index ?? text.length;

  const beforeConfig = text.slice(0, configStart);
  let afterConfig: string;
  if (instructionsOverride != null) {
    const instrSep = matches[3];
    const headerBetween = text.slice(configEnd, (instrSep.index ?? 0) + instrSep[0].length);
    afterConfig = headerBetween + instructionsOverride;
  } else {
    afterConfig = text.slice(configEnd);
  }

  const headerBlock = text.slice(configStart, configEnd);
  const lines = headerBlock.split('\n');

  const rebuilt: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const labelMatch = line.match(/^([A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?)\s*:\s*$/);
    if (labelMatch) {
      const rawLabel = labelMatch[1].trim();
      const cleanLabel = rawLabel.replace(/\s*\(optional\)\s*/i, '').trim();
      const key = cleanLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      rebuilt.push(line);
      const val = (fieldValues[key] || '').trim();
      if (val) {
        rebuilt.push('  ' + val.split('\n').join('\n  '));
      } else {
        while (++i < lines.length) {
          const dl = lines[i];
          if (dl.match(/^[A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?\s*:\s*$/) || dl.match(/═{3,}/)) break;
          rebuilt.push(dl);
        }
        continue;
      }
      i++;
      while (i < lines.length) {
        const dl = lines[i];
        if (dl.match(/^[A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?\s*:\s*$/) || dl.match(/═{3,}/)) break;
        i++;
      }
    } else {
      rebuilt.push(line);
      i++;
    }
  }

  return beforeConfig + rebuilt.join('\n') + afterConfig;
}

function extractValuesFromFilledPrompt(
  filledPrompt: string,
  fields: TemplateField[],
): Record<string, string> {
  const vals: Record<string, string> = {};
  for (const f of fields) {
    const escapedLabel = f.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const labelPattern = new RegExp(
      `(?:^|\\n)\\s*${escapedLabel}(?:\\s*\\(optional\\))?\\s*:\\s*\\n`,
      'i',
    );
    const match = filledPrompt.match(labelPattern);
    if (!match || match.index == null) continue;

    const afterLabel = filledPrompt.slice(match.index + match[0].length);
    const valueLines: string[] = [];
    for (const line of afterLabel.split('\n')) {
      if (line.match(/^[A-Z][A-Za-z /&'()\-.,]+(?:\([^)]*\))?\s*:\s*$/) || line.match(/═{3,}/)) break;
      valueLines.push(line.replace(/^ {2}/, ''));
    }
    const val = valueLines.join('\n').trim();
    if (val) vals[f.key] = val;
  }
  return vals;
}

function tryMatchTemplate(
  filledPrompt: string,
  templates: PromptTemplate[],
): { template: PromptTemplate; fields: TemplateField[]; values: Record<string, string>; instructions: string } | null {
  for (const tpl of templates) {
    const parsed = parseTemplateFields(tpl.prompt);
    if (!parsed || parsed.fields.length === 0) continue;

    const firstFieldLabel = parsed.fields[0].label;
    const escapedLabel = firstFieldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!new RegExp(escapedLabel, 'i').test(filledPrompt)) continue;

    const hasSeparators = /═{3,}/.test(filledPrompt);
    if (!hasSeparators) continue;

    const values = extractValuesFromFilledPrompt(filledPrompt, parsed.fields);
    if (Object.keys(values).length === 0) continue;

    const instrParsed = parseTemplateFields(filledPrompt);
    const instructions = instrParsed?.instructions ?? parsed.instructions ?? '';

    return { template: tpl, fields: parsed.fields, values, instructions };
  }
  return null;
}

export const ChainWizard: React.FC<ChainWizardProps> = ({ open, onClose, onChainCreated, initialPrompt, templateSlug, existingChainId, existingChainName, existingChainDescription, existingPlan, seedPrompt }) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState(0);
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [promptBackup, setPromptBackup] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<ChainPlan | null>(null);
  const [chainName, setChainName] = useState('');
  const [chainDescription, setChainDescription] = useState('');
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<PromptTemplate | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(TEMPLATE_CATEGORIES[0]?.label ?? null);
  const [templateFields, setTemplateFields] = useState<TemplateField[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [showRawPrompt, setShowRawPrompt] = useState(false);
  const [chainInstructions, setChainInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [includeFunctions, setIncludeFunctions] = useState(false);
  const [builderPlugins, setBuilderPlugins] = useState<BuilderPlugin[]>([]);
  const [builderPluginsLoading, setBuilderPluginsLoading] = useState(false);
  const [builderPluginsError, setBuilderPluginsError] = useState<string | null>(null);
  const [knowledgeCategories, setKnowledgeCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [paletteOpen, setPaletteOpen] = useState(readPaletteOpen);
  const [openAdvanced, setOpenAdvanced] = useState<Record<number, boolean>>({});
  const [needsCleanupHint, setNeedsCleanupHint] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const promptAnchorRef = useRef<HTMLDivElement | null>(null);

  const isEditMode = Boolean(initialPrompt || existingChainId || existingPlan);
  const hasFieldValues = Object.values(fieldValues).some(v => v.trim());
  const isUnmodifiedTemplate = activeTemplate != null && description === activeTemplate.prompt && !hasFieldValues;

  React.useEffect(() => {
    if (!open) return;
    fetch('/api/v2/chains/prompt-templates')
      .then(r => r.ok ? r.json() : [])
      .then(data => setPromptTemplates(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setBuilderPluginsLoading(true);
      setBuilderPluginsError(null);
      try {
        const token = await user?.getIdToken();
        const res = await fetch('/api/v2/plugins', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Could not load plugins (${res.status})`);
        const json = await res.json();
        const list: BuilderPlugin[] = Array.isArray(json?.plugins) ? json.plugins : [];
        list.sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
        if (!cancelled) setBuilderPlugins(list);
      } catch (e: any) {
        if (!cancelled) setBuilderPluginsError(e?.message || 'Failed to load plugins');
      } finally {
        if (!cancelled) setBuilderPluginsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await user?.getIdToken();
        if (!token) return;
        const res = await fetch('/api/v2/customers/me/categories', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data
          : Array.isArray(data?.categories) ? data.categories
          : Array.isArray(data?.data?.items) ? data.data.items
          : Array.isArray(data?.items) ? data.items
          : [];
        if (!cancelled) setKnowledgeCategories(list.filter((c: any) => c?.id).map((c: any) => ({ id: String(c.id), name: c.name || String(c.id) })));
      } catch { /* the Knowledge picker just shows no categories */ }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  React.useEffect(() => {
    if (!open) return;
    if (existingPlan) {
      setPlan(existingPlan);
      setChainName(existingChainName || existingPlan.chain_name || '');
      setChainDescription(existingChainDescription || existingPlan.chain_description || '');
      setDescription(initialPrompt || '');
      setPhase(1);
    } else if (initialPrompt) {
      setDescription(initialPrompt);
      setPhase(0);
    } else if (seedPrompt) {
      setDescription(seedPrompt);
      setPhase(0);
    }
    if (existingChainName && !existingPlan) setChainName(existingChainName);
    if (existingChainDescription && !existingPlan) setChainDescription(existingChainDescription);
  }, [open, initialPrompt, existingPlan, existingChainName, existingChainDescription, seedPrompt]);

  React.useEffect(() => {
    if (!open || !initialPrompt || promptTemplates.length === 0 || activeTemplate) return;
    if (existingPlan) return;

    let matched: ReturnType<typeof tryMatchTemplate> = null;

    if (templateSlug) {
      const bySlug = promptTemplates.find(t => t.slug === templateSlug);
      if (bySlug) {
        const parsed = parseTemplateFields(bySlug.prompt);
        if (parsed && parsed.fields.length > 0) {
          const values = extractValuesFromFilledPrompt(initialPrompt, parsed.fields);
          if (Object.keys(values).length > 0) {
            const instrParsed = parseTemplateFields(initialPrompt);
            matched = { template: bySlug, fields: parsed.fields, values, instructions: instrParsed?.instructions ?? parsed.instructions ?? '' };
          }
        }
      }
    }

    if (!matched) {
      matched = tryMatchTemplate(initialPrompt, promptTemplates);
    }

    if (matched) {
      setActiveTemplate(matched.template);
      setTemplateFields(matched.fields);
      setFieldValues(matched.values);
      setChainInstructions(matched.instructions);
      setShowRawPrompt(false);
      setShowInstructions(false);
    }
  }, [open, initialPrompt, templateSlug, promptTemplates, activeTemplate, existingPlan]);

  const resetState = () => {
    setPhase(0);
    setDescription('');
    setGenerating(false);
    setCleaning(false);
    setPromptBackup(null);
    setSaving(false);
    setSaveProgress('');
    setError(null);
    setPlan(null);
    setChainName('');
    setChainDescription('');
    setActiveTemplate(null);
    setTemplateFields([]);
    setFieldValues({});
    setChainInstructions('');
    setShowInstructions(false);
    setShowRawPrompt(false);
    setIncludeFunctions(false);
    setNeedsCleanupHint(false);
  };

  const handleClose = () => { resetState(); onClose(); };

  const closeMention = () => {
    setMentionQuery(null);
    setMentionStart(null);
  };

  const insertSnippet = useCallback((markdown: string, replaceFrom?: number, replaceTo?: number) => {
    if (activeTemplate && !showRawPrompt) setShowRawPrompt(true);
    const el = descriptionRef.current;
    const current = description;
    const block = markdown.trim().replace(/\s+$/, '') + '\n\n';
    const start = replaceFrom ?? el?.selectionStart ?? current.length;
    const end = replaceTo ?? el?.selectionEnd ?? start;
    const before = current.slice(0, start);
    const after = current.slice(end);
    const lead = before.length > 0 && !before.endsWith('\n') ? '\n\n' : (before.endsWith('\n') && !before.endsWith('\n\n') ? '\n' : '');
    const next = before + lead + block + after;
    setDescription(next);
    setError(null);
    setNeedsCleanupHint(true);
    setMentionQuery(null);
    setMentionStart(null);
    requestAnimationFrame(() => {
      const node = descriptionRef.current;
      if (!node) return;
      const pos = (before + lead + block).length;
      node.focus();
      node.setSelectionRange(pos, pos);
    });
  }, [activeTemplate, showRawPrompt, description]);

  const mentionItems = mentionQuery != null
    ? builderMentionItems(builderPlugins)
        .filter((item) => item.label.toLowerCase().includes(mentionQuery.toLowerCase()) || item.id.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 10)
    : [];

  const onPromptChange = (val: string, caret: number) => {
    setDescription(val);
    setError(null);
    const before = val.slice(0, caret);
    const m = before.match(/(^|[\n\s])@([a-zA-Z0-9_:-]*)$/);
    if (m) {
      setMentionStart(caret - m[2].length - 1);
      setMentionQuery(m[2]);
    } else {
      closeMention();
    }
  };

  // ── Phase 1: Generate ─────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!description.trim() || description.trim().length < 10) {
      setError('Please provide a more detailed description (at least 10 characters).');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const token = await user?.getIdToken();
      const res = await fetch('/api/v2/chains/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ description: description.trim(), preferred_text_model: 'gemini-3-5-flash-lite', preferred_image_model: 'nano-banana-2', include_functions: includeFunctions }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error (${res.status})`);
      }
      const data: ChainPlan = await res.json();

      // Normalise: if the LLM marked a step as "existing_function" but gave no valid
      // existing_id (or it looks like a hallucinated UUID), convert to ai_function.
      data.steps = data.steps.map(s => {
        if (s.step_type === 'existing_function' && s.existing_id) {
          const looksLikePlugin = s.existing_id.startsWith('plugin:');
          const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(s.existing_id);
          if (!looksLikePlugin && !looksLikeUuid) {
            return { ...s, step_type: 'ai_function' as const, existing_id: null };
          }
        }
        return s;
      });

      // Regenerating shouldn't drop knowledge the user already linked.
      const linkedKnowledge = new Map(
        (plan?.steps || []).filter(s => s.knowledge_categories?.length).map(s => [s.name, s])
      );
      data.steps = data.steps.map(s => {
        const prev = linkedKnowledge.get(s.name);
        return prev && !s.knowledge_categories?.length
          ? { ...s, knowledge_categories: prev.knowledge_categories, knowledge_chunks: prev.knowledge_chunks, knowledge_query: prev.knowledge_query }
          : s;
      });

      setPlan(data);
      setChainName(data.chain_name);
      setChainDescription(data.chain_description);
      setPhase(1);
    } catch (e: any) {
      setError(e.message || 'Failed to generate chain plan.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCleanupPrompt = async () => {
    if (!description.trim() || description.trim().length < 10) {
      setError('Add a bit more detail first (at least 10 characters), then clean up.');
      return;
    }
    if (activeTemplate && !showRawPrompt) setShowRawPrompt(true);
    setCleaning(true);
    setError(null);
    const previous = description;
    try {
      const token = await user?.getIdToken();
      const res = await fetch('/api/v2/chains/cleanup-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ description: description.trim(), include_functions: includeFunctions }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error (${res.status})`);
      }
      const data = await res.json();
      const cleaned = (data?.prompt || '').trim();
      if (!cleaned) throw new Error('Cleanup returned an empty prompt.');
      setPromptBackup(previous);
      setDescription(cleaned);
      setNeedsCleanupHint(false);
    } catch (e: any) {
      setError(e.message || 'Failed to clean up the prompt.');
    } finally {
      setCleaning(false);
    }
  };

  const undoCleanup = () => {
    if (promptBackup == null) return;
    setDescription(promptBackup);
    setPromptBackup(null);
    setNeedsCleanupHint(true);
  };

  const togglePalette = () => {
    setPaletteOpen((open) => {
      const next = !open;
      writePaletteOpen(next);
      return next;
    });
  };

  // ── Phase 2: Edit helpers ─────────────────────────────────────────────────

  const updateStep = (idx: number, patch: Partial<ChainStep>) => {
    if (!plan) return;
    const steps = [...plan.steps];
    steps[idx] = { ...steps[idx], ...patch };
    setPlan({ ...plan, steps });
  };

  const rebuildEdges = (steps: ChainStep[]) => {
    if (steps.length <= 1) return [];
    return steps.slice(0, -1).map((s, i) => ({
      id: `e${i}`, source: `step-${s.step_number}`, target: `step-${steps[i + 1].step_number}`,
    }));
  };

  const removeStep = (idx: number) => {
    if (!plan) return;
    const steps = plan.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
    setPlan({ ...plan, steps, edges: rebuildEdges(steps) });
  };

  const addStep = () => {
    if (!plan) return;
    const num = plan.steps.length + 1;
    const newStep: ChainStep = {
      step_number: num, name: `step_${num}`, display_name: `Step ${num}`,
      description: '', step_type: 'ai_function', existing_id: null,
      instructions: '', input_types: ['text'], allowed_models: ['gemini-3-5-flash-lite'],
      response_type: 'text', return_object: null, map_over_array: false,
      array_field: null, skip_item_when_empty: null, input_template: null,
      temperature: 0,
    };
    const steps = [...plan.steps, newStep];
    setPlan({ ...plan, steps, edges: rebuildEdges(steps) });
  };

  const convertToNewFunction = (idx: number) => {
    updateStep(idx, { step_type: 'ai_function', existing_id: null, instructions: plan?.steps[idx].instructions || '' });
  };

  // ── Phase 3: Save ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!plan || !chainName.trim()) { setError('Please provide a chain name.'); return; }
    setSaving(true); setError(null); setSaveProgress('Starting...');
    try {
      const token = await user?.getIdToken();
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const functionIdMap: Record<number, string> = {};

      // Unique suffix for this chain generation run — prevents name collisions
      // with custom objects and functions from previous runs.
      const uniqueSuffix = Date.now().toString(36).slice(-5);

      for (let i = 0; i < plan.steps.length; i++) {
        const step = plan.steps[i];

        if ((step.step_type === 'plugin' || step.step_type === 'existing_function') && step.existing_id) {
          functionIdMap[step.step_number] = step.existing_id;
          setSaveProgress(`Step ${i + 1}/${plan.steps.length}: Reusing ${step.step_type === 'plugin' ? step.existing_id : 'existing function'}`);
          continue;
        }

        setSaveProgress(`Step ${i + 1}/${plan.steps.length}: Creating "${step.display_name}"...`);
        let responseFormat: any = null;
        let responseObjectId: string | null = null;

        if (step.return_object?.fields?.length && (step.response_type === 'object' || step.response_type === 'list')) {
          const baseName = step.return_object.name || step.name + '_output';
          const uniqueName = `${baseName}_${uniqueSuffix}`;

          // Map LLM-generated types to valid custom object field_type enum values
          const fixFieldType = (t: string): string => {
            const map: Record<string, string> = {
              number: 'float', int: 'integer', num: 'float',
              bool: 'boolean', str: 'string', object: 'json',
              list: 'array', dict: 'json', url: 'string',
            };
            const lower = (t || 'string').toLowerCase().trim();
            return map[lower] || (['string','integer','float','boolean','date','datetime','json','text','uuid','array','reference'].includes(lower) ? lower : 'string');
          };

          const objPayload = {
            name: uniqueName,
            display_name: step.display_name + ' Output',
            description: `Return schema for ${step.display_name}`,
            fields: step.return_object.fields.map((f: ReturnField) => ({
              name: f.name,
              display_name: f.name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              field_type: fixFieldType(f.type), description: f.description || '', required: f.required !== false,
            })),
          };
          const objRes = await fetch('/api/v2/custom-objects/', { method: 'POST', headers, body: JSON.stringify(objPayload) });
          if (objRes.ok) {
            const objData = await objRes.json();
            const objId = objData.id || objData.data?.id;
            if (objId) {
              responseObjectId = objId;
              responseFormat = { type: step.response_type === 'list' ? 'array' : 'object', object_id: objId, object_name: uniqueName, is_list: step.response_type === 'list' };
            }
          }
        }

        // Normalise input_types: replace "object" with "json", strip invalid values
        const validInputTypes = new Set(['text','json','image','video','audio','mp3','pdf','csv','xml','html','markdown','binary']);
        const fixedInputTypes = (step.input_types || ['text'])
          .map((t: string) => t === 'object' ? 'json' : t.toLowerCase().trim())
          .filter((t: string) => validInputTypes.has(t));

        const funcPayload: any = {
          name: `${step.name}_${uniqueSuffix}`,
          display_name: step.display_name,
          description: step.description,
          instructions: step.instructions || '',
          input_types: fixedInputTypes.length > 0 ? fixedInputTypes : ['text'],
          allowed_models: step.allowed_models.length > 0 ? step.allowed_models : [...DEFAULT_ALLOWED_MODELS],
          response_format: responseFormat, is_public: false,
          metadata: { response_type: step.response_type, response_object_id: responseObjectId, is_list_response: step.response_type === 'list', created_by_chain_wizard: true, ...(step.enable_google_search ? { enable_google_search: true } : {}) },
        };
        const funcRes = await fetch('/api/v2/ai-functions/', { method: 'POST', headers, body: JSON.stringify(funcPayload) });
        if (!funcRes.ok) {
          const err = await funcRes.json().catch(() => ({}));
          const msg = typeof err.detail === 'string' ? err.detail
            : Array.isArray(err.detail) ? err.detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
            : JSON.stringify(err.detail || err);
          throw new Error(`Failed to create "${step.display_name}": ${msg}`);
        }
        const funcData = await funcRes.json();
        const funcId = funcData.id || funcData.data?.id;
        if (!funcId) throw new Error(`No ID returned for "${step.display_name}"`);
        functionIdMap[step.step_number] = String(funcId);
      }

      // Wait briefly for LLM service route registration to complete.
      // Each function creation triggers an async route reload on the LLM service,
      // but if we create the chain and execute immediately the routes may not be
      // registered yet (causing 404s). A short pause lets the reloads finish.
      setSaveProgress('Registering functions with AI service...');
      await new Promise(r => setTimeout(r, 3000));

      setSaveProgress('Building chain...');

      // Rewrite step-N references to node-N in template values so the
      // chain executor can resolve them (node IDs are node-1, node-2, etc.)
      const fixTemplateRefs = (tpl: Record<string, any> | null): Record<string, any> | null => {
        if (!tpl) return null;
        const json = JSON.stringify(tpl);
        const fixed = json.replace(/\{\{step-(\d+)/g, '{{node-$1');
        try { return JSON.parse(fixed); } catch { return tpl; }
      };

      // Normalise an edge endpoint: accept "step-3", "node-3", or "3" → "node-3"
      const toNodeId = (ref: string): string => {
        const num = ref.replace(/^(step|node)-/, '');
        return `node-${num}`;
      };

      // Build a lookup of which nodes feed into each node (from edges)
      const normalizedEdges = plan.edges.map(e => ({
        source: toNodeId(e.source),
        target: toNodeId(e.target),
      }));

      // For arrayPath: find the correct source node that actually contains
      // the array field, rather than blindly using step_number - 1.
      const findArraySourceNode = (step: ChainStep): string => {
        const nodeId = `node-${step.step_number}`;
        const parentNodeIds = normalizedEdges
          .filter(e => e.target === nodeId)
          .map(e => e.source);

        // Check input_template for references to this array field from a
        // specific node (e.g. {{node-6.clips}} means the array is on node-6)
        if (step.input_template) {
          const tplStr = JSON.stringify(step.input_template);
          const refPattern = new RegExp(`\\{\\{(?:node|step)-(\\d+)\\.${step.array_field}\\}\\}`);
          const match = tplStr.match(refPattern);
          if (match) return `node-${match[1]}`;
        }

        // Check which parent step's return_object contains this array field
        for (const parentId of parentNodeIds) {
          const parentNum = parseInt(parentId.replace('node-', ''), 10);
          const parentStep = plan.steps.find(s => s.step_number === parentNum);
          if (parentStep?.return_object?.fields) {
            const hasField = parentStep.return_object.fields.some(
              f => f.name === step.array_field && (f.type === 'array' || f.type === 'list')
            );
            if (hasField) return parentId;
          }
        }

        // Fallback: use first parent, or step_number - 1
        return parentNodeIds[0] || `node-${step.step_number - 1}`;
      };

      const nodes = plan.steps.map((step, idx) => {
        const tpl = step.input_template ? fixTemplateRefs(step.input_template) : null;
        const batchModel = isLlmBatchPlugin(step.existing_id) ? (step.allowed_models[0] || null) : null;
        const inputTemplate = (batchModel && tpl && typeof tpl === 'object')
          ? { ...tpl, model: batchModel }
          : tpl;
        return {
        id: `node-${step.step_number}`,
        functionId: functionIdMap[step.step_number],
        position: { x: 100 + idx * 300, y: 200 },
        config: {
          mapOverArray: step.map_over_array || false,
          ...(step.array_field ? { arrayPath: `${findArraySourceNode(step)}.${step.array_field}` } : {}),
          ...(step.skip_item_when_empty ? { skipItemWhenEmpty: step.skip_item_when_empty } : {}),
          ...(inputTemplate ? { inputTemplate } : {}),
          ...(step.enable_google_search ? { enableGoogleSearch: true } : {}),
          ...(step.timeout_seconds ? { timeoutSeconds: step.timeout_seconds } : {}),
          ...(typeof step.temperature === 'number'
            ? { temperature: step.temperature }
            : (!step.existing_id || !String(step.existing_id).startsWith('plugin:'))
              ? { temperature: 0 }
              : {}),
          ...(step.optional ? { optional: true } : {}),
          ...(batchModel ? { modelOverride: batchModel } : {}),
          ...(step.step_type !== 'plugin' && step.knowledge_categories?.length ? {
            knowledgeCategories: step.knowledge_categories,
            knowledgeChunks: step.knowledge_chunks ?? 3,
            ...(step.knowledge_query?.trim()
              ? { knowledgeQuery: step.knowledge_query.trim().replace(/\{\{step-(\d+)/g, '{{node-$1') }
              : {}),
          } : {}),
        },
      };
      });
      const edges = plan.edges.length > 0
        ? plan.edges.map((e, i) => ({
            id: e.id || `e-${i}`,
            source: toNodeId(e.source),
            target: toNodeId(e.target),
            sourceHandle: 'output', targetHandle: 'input', type: 'smoothstep',
          }))
        : plan.steps.length > 1
          ? plan.steps.slice(0, -1).map((step, idx) => ({
              id: `e-${idx}`, source: `node-${step.step_number}`, target: `node-${plan.steps[idx + 1].step_number}`,
              sourceHandle: 'output', targetHandle: 'input', type: 'smoothstep',
            }))
          : [];

      const chainConfig: Record<string, any> = {
        generation_prompt: description,
        wizard_prompt: description,
      };
      const slug = activeTemplate?.slug || templateSlug;
      if (slug) {
        chainConfig.template_slug = slug;
      }
      if (plan.runtime_inputs && plan.runtime_inputs.length > 0) {
        chainConfig.runtime_inputs = plan.runtime_inputs;
      }
      const chainPayload = { name: chainName, description: chainDescription, graph_data: { nodes, edges }, config: chainConfig };
      const chainUrl = existingChainId ? `/api/v2/chains/${existingChainId}` : '/api/v2/chains/';
      const chainMethod = existingChainId ? 'PUT' : 'POST';
      const chainRes = await fetch(chainUrl, { method: chainMethod, headers, body: JSON.stringify(chainPayload) });
      if (!chainRes.ok) { const err = await chainRes.json().catch(() => ({})); throw new Error(`Failed to ${existingChainId ? 'update' : 'create'} chain: ${err.detail || chainRes.status}`); }
      const chainData = await chainRes.json();
      const chainId = existingChainId || chainData.id || chainData.data?.id;
      setSaveProgress('Done!'); setPhase(2);
      setTimeout(() => { handleClose(); if (chainId) onChainCreated(String(chainId)); }, 800);
    } catch (e: any) { setError(e.message || 'Failed to save chain.'); } finally { setSaving(false); }
  };

  // ── Render: Phase 1 ───────────────────────────────────────────────────────

  const renderDescribePhase = () => (
    <Box sx={{ pt: 1, display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: { xs: 'column', md: 'row' } }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {(generating || cleaning) && (
        <Paper elevation={0} sx={{
          p: 3, mb: 2, textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)',
          border: '1px solid', borderColor: 'primary.light', borderRadius: 2,
        }}>
          <CircularProgress size={36} sx={{ mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {cleaning ? 'Cleaning up your spec…' : 'AI is designing your chain...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cleaning
              ? 'Rewriting your notes and inserted plugins into a paste-complete builder spec. You can still edit it before generating the chain.'
              : 'Analyzing your requirements and building the optimal function chain. This may take up to 3 minutes for complex templates.'}
          </Typography>
        </Paper>
      )}
      <Box sx={{
        ...((generating || cleaning) ? { pointerEvents: 'none', opacity: 0.45, filter: 'grayscale(30%)', transition: 'all 0.3s ease' } : {}),
      }}>
      {isEditMode && !activeTemplate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is the prompt that originally generated this chain. Edit it and click <strong>Regenerate</strong> to create an updated plan.
        </Alert>
      )}

      {/* Template header — shown in both create and edit mode when a template is active */}
      {promptTemplates.length > 0 && activeTemplate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Template: {activeTemplate.name}
          </Typography>
          {isEditMode && (
            <Chip label="Edit raw prompt" size="small" variant="outlined" onClick={() => setShowRawPrompt(true)} sx={{ height: 24 }} />
          )}
          {!isEditMode && (
            <Chip
              label="Clear template"
              size="small"
              variant="outlined"
              onDelete={() => { setActiveTemplate(null); setDescription(''); setTemplateFields([]); setFieldValues({}); setShowRawPrompt(false); setChainInstructions(''); setShowInstructions(false); }}
              sx={{ height: 24 }}
            />
          )}
        </Box>
      )}

      {!isEditMode && promptTemplates.length > 0 && !activeTemplate && (
        <Box sx={{ mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
              Start from a template
            </Typography>
          </Box>

            {(() => {
              const slugIndex = new Map(promptTemplates.map((t) => [t.slug, t]));
              const categorized = new Set<string>();

              const selectTemplate = (tpl: PromptTemplate) => {
                setActiveTemplate(tpl);
                setError(null);
                const parsed = parseTemplateFields(tpl.prompt);
                const fields = parsed?.fields || [];
                setTemplateFields(fields);
                const defaults = loadFieldDefaults(fields);
                setFieldValues(defaults);
                const hasDefaults = Object.values(defaults).some(v => v.trim());
                setDescription(hasDefaults ? rebuildPromptFromFields(tpl, defaults) : tpl.prompt);
                setChainInstructions(parsed?.instructions ?? '');
                setShowInstructions(false);
                setShowRawPrompt(false);
              };

              const renderTemplateCard = (tpl: PromptTemplate) => {
                const icon = TEMPLATE_ICONS[tpl.slug] || <AutoAwesomeIcon />;
                return (
                  <Tooltip key={tpl.slug} title={tpl.description} placement="top" arrow enterDelay={400}>
                    <Paper
                      elevation={0}
                      onClick={() => selectTemplate(tpl)}
                      sx={{
                        p: 1.25, cursor: 'pointer', border: '1px solid', borderColor: 'divider',
                        borderRadius: 2, transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                          boxShadow: '0 2px 8px rgba(102,126,234,0.15)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 30, height: 30, bgcolor: 'primary.main', flexShrink: 0, mt: 0.25,
                            '& .MuiSvgIcon-root': { fontSize: 17 },
                          }}
                        >
                          {icon}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {tpl.name}
                          </Typography>
                          <Typography
                            variant="caption" color="text.secondary"
                            sx={{
                              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                              overflow: 'hidden', lineHeight: 1.35, mt: 0.2, fontSize: '0.7rem',
                            }}
                          >
                            {tpl.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Tooltip>
                );
              };

              const allCategories = [...TEMPLATE_CATEGORIES];
              const uncategorizedTemplates = promptTemplates.filter((t) => {
                const inCategory = TEMPLATE_CATEGORIES.some((cat) => cat.slugs.includes(t.slug));
                return !inCategory;
              });
              if (uncategorizedTemplates.length > 0) {
                allCategories.push({ label: 'Other', slugs: uncategorizedTemplates.map((t) => t.slug) });
              }

              return (
                <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                  {allCategories.map((cat) => {
                    const catTemplates = cat.slugs.map((s) => slugIndex.get(s)).filter(Boolean) as typeof promptTemplates;
                    if (catTemplates.length === 0) return null;
                    catTemplates.forEach((t) => categorized.add(t.slug));
                    const isOpen = expandedCategory === cat.label;
                    return (
                      <Box key={cat.label} sx={{ mb: 0.5 }}>
                        <Box
                          onClick={() => setExpandedCategory(isOpen ? null : cat.label)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            py: 0.75, px: 1.5, cursor: 'pointer', borderRadius: 1.5,
                            userSelect: 'none',
                            borderLeft: isOpen ? '3px solid' : '3px solid transparent',
                            borderLeftColor: isOpen ? 'primary.main' : 'transparent',
                            bgcolor: isOpen ? 'action.selected' : 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': { bgcolor: isOpen ? 'action.selected' : 'action.hover' },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, fontSize: '0.82rem', color: isOpen ? 'primary.main' : 'text.primary', flexGrow: 1 }}
                          >
                            {cat.label}
                          </Typography>
                          <Chip label={catTemplates.length} size="small" variant={isOpen ? 'filled' : 'outlined'} color={isOpen ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }} />
                          <ExpandMoreIcon
                            sx={{
                              fontSize: 18, color: isOpen ? 'primary.main' : 'text.secondary', transition: 'transform 0.2s',
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </Box>
                        <Collapse in={isOpen} timeout={200}>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: 1, px: 0.5, pt: 0.75, pb: 1,
                          }}>
                            {catTemplates.map(renderTemplateCard)}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
        </Box>
      )}

      {!activeTemplate && promptTemplates.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1.5 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, flexShrink: 0 }}>
            or describe your own
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>
      )}

      {/* Dynamic form for template fields */}
      {activeTemplate && templateFields.length > 0 && !showRawPrompt ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Fill in the fields below to configure your chain.
            </Typography>
            <Chip
              label="Edit raw prompt"
              size="small"
              variant="outlined"
              onClick={() => {
                if (hasFieldValues) setDescription(rebuildPromptFromFields(activeTemplate, fieldValues, chainInstructions || undefined));
                setShowRawPrompt(true);
              }}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {templateFields.map((field) => {
              if (field.key === 'enable_web_research') {
                const isOn = (fieldValues[field.key] || '').toLowerCase() === 'true';
                return (
                  <FormControlLabel
                    key={field.key}
                    label={`${field.label} — supplement content planning with live web results`}
                    control={
                      <Switch
                        checked={isOn}
                        onChange={(e) => {
                          const val = e.target.checked ? 'true' : 'false';
                          const next = { ...fieldValues, [field.key]: val };
                          setFieldValues(next);
                          setDescription(rebuildPromptFromFields(activeTemplate!, next, chainInstructions || undefined));
                          saveFieldValue(field.storageKey, val);
                        }}
                      />
                    }
                    sx={{ ml: 0 }}
                  />
                );
              }
              return (
                <TextField
                  key={field.key}
                  fullWidth
                  size="small"
                  label={field.label + (field.optional ? ' (optional)' : '')}
                  placeholder={field.placeholder}
                  multiline={field.multiline}
                  minRows={field.multiline ? 3 : undefined}
                  maxRows={field.multiline ? 8 : undefined}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const next = { ...fieldValues, [field.key]: val };
                    setFieldValues(next);
                    setDescription(rebuildPromptFromFields(activeTemplate!, next, chainInstructions || undefined));
                    saveFieldValue(field.storageKey, val);
                    setError(null);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              );
            })}
          </Box>

          {!hasFieldValues && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<AutoAwesomeIcon fontSize="inherit" />}>
              Fields are optional — fill them in to customize the chain, or click <strong>Generate</strong> to use the template defaults.
            </Alert>
          )}

          {/* Collapsible chain instructions editor */}
          <Box
            sx={{
              mt: 2.5,
              border: '1px solid',
              borderColor: showInstructions ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
            }}
          >
            <Box
              onClick={() => setShowInstructions(v => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                gap: 1,
                px: 1.5,
                py: 1,
                bgcolor: showInstructions ? 'action.selected' : 'grey.50',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <TuneIcon sx={{ fontSize: 18, color: showInstructions ? 'primary.main' : 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: showInstructions ? 'primary.main' : 'text.primary' }}>
                Chain Instructions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                — customize what the AI planner builds
              </Typography>
              <ExpandMoreIcon
                sx={{
                  ml: 'auto',
                  fontSize: 20,
                  color: 'text.secondary',
                  transition: 'transform 0.2s',
                  transform: showInstructions ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
            <Collapse in={showInstructions}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                value={chainInstructions}
                onChange={(e) => {
                  const val = e.target.value;
                  setChainInstructions(val);
                  setDescription(rebuildPromptFromFields(activeTemplate!, fieldValues, val));
                }}
                variant="outlined"
                size="small"
                sx={{
                  m: 1.5,
                  mt: 0.5,
                  width: 'calc(100% - 24px)',
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  '& .MuiInputBase-input': { fontSize: '0.82rem' },
                }}
                placeholder="Chain planner instructions..."
              />
            </Collapse>
          </Box>
        </Box>
      ) : (
        <>
          {activeTemplate && showRawPrompt && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Chip
                label="Back to form"
                size="small"
                variant="outlined"
                onClick={() => setShowRawPrompt(false)}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            </Box>
          )}
          <Box ref={promptAnchorRef}>
          <TextField fullWidth multiline minRows={8} maxRows={18} value={description}
            inputRef={descriptionRef}
            onChange={(e) => onPromptChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeMention();
              if (e.key === 'Enter' && mentionQuery != null && mentionItems[0]) {
                e.preventDefault();
                const start = mentionStart ?? description.length;
                const el = descriptionRef.current;
                const caret = el?.selectionStart ?? description.length;
                insertSnippet(mentionItems[0].markdown, start, caret);
              }
            }}
            variant="outlined" sx={{ mb: 0.5 }}
            placeholder={activeTemplate
              ? 'Fill in the configuration section at the top, then generate.'
              : 'Describe the chain, or click Insert in the panel. Type @ to search plugins (e.g. @csv_to_json).'
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: needsCleanupHint ? 1 : 2 }}>
            Type <strong>@</strong> to search every plugin and insert it. Then <strong>Clean up prompt</strong> turns messy inserts into a full spec; <strong>Generate</strong> builds the chain from that spec.
          </Typography>
          {needsCleanupHint && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => setNeedsCleanupHint(false)}
            >
              Inserted snippets use <strong>THIS / PREV / NEXT</strong> so they stay valid no matter where you drop them. Click <strong>Clean up prompt</strong> when you are done inserting — it will number the steps (node-1, node-2, …) and connect them so data flows.
            </Alert>
          )}
          <Popper open={Boolean(mentionQuery != null && mentionItems.length)} anchorEl={promptAnchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
            <ClickAwayListener onClickAway={closeMention}>
              <Paper elevation={6} sx={{ mt: 0.5, minWidth: 320, maxHeight: 280, overflow: 'auto' }}>
                <List dense disablePadding>
                  {mentionItems.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => {
                        const start = mentionStart ?? description.length;
                        const el = descriptionRef.current;
                        const caret = el?.selectionStart ?? description.length;
                        insertSnippet(item.markdown, start, caret);
                      }}
                    >
                      <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </ClickAwayListener>
          </Popper>
          </Box>

          {isUnmodifiedTemplate && !showRawPrompt && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<AutoAwesomeIcon fontSize="inherit" />}>
              You can submit this template <strong>as-is</strong>, or fill in the configuration section at the top to customize the chain — both work.
            </Alert>
          )}
        </>
      )}

      </Box>
    </Box>
      <Box
        sx={{
          width: { xs: '100%', md: paletteOpen ? 400 : 52 },
          flexShrink: 0,
          borderLeft: { md: '1px solid' },
          borderColor: { md: 'divider' },
          pl: { md: paletteOpen ? 2 : 0.5 },
          pt: { xs: 1, md: 0 },
          maxHeight: { md: '64vh' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: paletteOpen ? 280 : { xs: 0, md: 120 },
          transition: 'width 0.2s ease',
        }}
      >
        {paletteOpen ? (
          <BuilderPalette
            plugins={builderPlugins}
            loading={builderPluginsLoading}
            loadError={builderPluginsError}
            onInsert={(md) => insertSnippet(md)}
            disabled={generating || cleaning}
            onHide={togglePalette}
          />
        ) : (
          <Tooltip title="Show insert tools" placement="left">
            <Button
              onClick={togglePalette}
              variant="outlined"
              sx={{
                textTransform: 'none',
                alignSelf: { xs: 'stretch', md: 'center' },
                minWidth: { md: 40 },
                px: { xs: 1.5, md: 0.75 },
                py: { xs: 1, md: 1.5 },
                flexDirection: { xs: 'row', md: 'column' },
                gap: 0.75,
                height: { md: '100%' },
                minHeight: { md: 160 },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
              <PluginIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  writingMode: { md: 'vertical-rl' },
                  transform: { md: 'rotate(180deg)' },
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  whiteSpace: 'nowrap',
                }}
              >
                Insert tools
              </Typography>
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  // ── Render: Step Card ─────────────────────────────────────────────────────

  const renderStepCard = (step: ChainStep, idx: number) => {
    const rtInfo = RESPONSE_TYPE_INFO[step.response_type] || RESPONSE_TYPE_INFO.text;
    const isNewFunction = step.step_type === 'ai_function';
    const isPlugin = step.step_type === 'plugin';
    const isBatchSubmit = isLlmBatchPlugin(step.existing_id);

    const isAiStep = !isPlugin;
    const hasSearch = isAiStep && (step.allowed_models[0] || '').startsWith('gemini');
    const activeAdvanced = [
      step.map_over_array && 'Map array',
      hasSearch && step.enable_google_search && 'Web search',
      isAiStep && step.timeout_seconds != null && `${step.timeout_seconds}s per attempt`,
    ].filter(Boolean) as string[];
    const advancedOpen = openAdvanced[step.step_number] ?? false;
    const existingFunctionId = !isNewFunction && !isPlugin ? step.existing_id : null;
    const linkedCategories = step.knowledge_categories || [];
    const categoryName = (id: string) => knowledgeCategories.find(c => c.id === id)?.name || id;

    const accent = isPlugin ? 'secondary.main' : isNewFunction ? 'primary.main' : 'grey.500';
    const isLast = idx === (plan?.steps.length ?? 0) - 1;

    return (
      <Box key={step.step_number} sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
          <Avatar sx={{ width: 30, height: 30, mt: 1.5, fontSize: '0.85rem', fontWeight: 700, bgcolor: accent }}>{idx + 1}</Avatar>
          {!isLast && <Box sx={{ flexGrow: 1, width: 2, my: 0.5, bgcolor: 'divider', borderRadius: 1 }} />}
        </Box>
      <Accordion defaultExpanded={(plan?.steps.length ?? 0) <= 3} disableGutters
        sx={{
          flexGrow: 1, minWidth: 0, mb: 2,
          '&:before': { display: 'none' },
          border: 1, borderColor: 'divider', borderLeft: 4, borderLeftColor: accent, borderRadius: 1.5,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          '&.Mui-expanded': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, minWidth: 0 },
            '&.Mui-expanded': { bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' },
          }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{step.display_name}</Typography>
            {step.description && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{step.description}</Typography>
            )}
          </Box>

          <Tooltip title={rtInfo.hint}>
            <Chip icon={rtInfo.icon as any} label={rtInfo.label} size="small" variant="outlined"
              sx={{ borderColor: rtInfo.color, color: rtInfo.color, '& .MuiChip-icon': { color: rtInfo.color } }} />
          </Tooltip>
          {isAiStep && step.allowed_models[0] && (
            <Chip label={step.allowed_models[0]} size="small" variant="outlined" sx={{ display: { xs: 'none', md: 'inline-flex' } }} />
          )}
          {isPlugin && <Chip icon={<PluginIcon />} label="Plugin" size="small" color="secondary" variant="outlined" />}
          {!isNewFunction && !isPlugin && <Chip label="Existing" size="small" variant="outlined" />}
          {isNewFunction && <Chip label="New" size="small" color="primary" variant="outlined" />}
          {step.map_over_array && <Chip icon={<SplitIcon />} label="Map Array" size="small" color="warning" />}
          {isAiStep && linkedCategories.length > 0 && (
            <Tooltip title={`Knowledge: ${linkedCategories.map(categoryName).join(', ')}`}>
              <Chip label={`Knowledge (${linkedCategories.length})`} size="small" color="success" variant="outlined" />
            </Tooltip>
          )}

          <Tooltip title="Remove step">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeStep(idx); }}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Grid container spacing={2}>
            <StepSection title="Step" first />
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Display Name" value={step.display_name}
                onChange={(e) => updateStep(idx, { display_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Function Name (internal)" value={step.name}
                onChange={(e) => updateStep(idx, { name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="What this step does" value={step.description}
                onChange={(e) => updateStep(idx, { description: e.target.value })} />
            </Grid>

            {!isNewFunction && step.existing_id && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { overflow: 'visible', width: '100%' },
                    '& .MuiAlert-action': { pt: 0, mr: 0, ml: 1, pl: 0 },
                  }}
                  action={
                    <Button size="small" color="primary" onClick={() => convertToNewFunction(idx)} sx={{ whiteSpace: 'nowrap' }}>
                      Convert to New Function
                    </Button>
                  }
                >
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    <strong>{isPlugin ? 'Plugin' : 'Existing Function'}:</strong>{' '}
                    {step.existing_id}
                  </Typography>
                  {step.instructions && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {step.instructions.substring(0, 200)}{step.instructions.length > 200 ? '...' : ''}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}

            <StepSection title={isPlugin ? 'Output' : 'Model & output'} />
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Output Type</InputLabel>
                <Select value={step.response_type} label="Output Type"
                  onChange={(e) => updateStep(idx, { response_type: e.target.value })}>
                  <MenuItem value="text"><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><TextIcon fontSize="small" /> Text</Box></MenuItem>
                  <MenuItem value="object"><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><ObjectIcon fontSize="small" /> Structured Object</Box></MenuItem>
                  <MenuItem value="list"><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><ListIcon fontSize="small" /> List of Objects</Box></MenuItem>
                  <MenuItem value="image"><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><ImageIcon fontSize="small" /> Image Generation</Box></MenuItem>
                  <MenuItem value="video"><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><VideoIcon fontSize="small" /> Video Generation</Box></MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!isPlugin && (
            <Grid item xs={12} md={4}>
              <ModelSelector
                value={step.allowed_models[0] || 'gemini-3-5-flash-lite'}
                onChange={(val) => updateStep(idx, { allowed_models: [val as string] })}
                options={WIZARD_MODEL_OPTIONS}
                size="small"
                label="AI Model"
              />
            </Grid>
            )}
            {!isPlugin && (
              <Grid item xs={12} md={4}>
                <TemperatureSlider
                  value={step.temperature}
                  model={step.allowed_models[0]}
                  onChange={(v) => updateStep(idx, { temperature: v })}
                />
              </Grid>
            )}

            {isBatchSubmit && (
              <Grid item xs={12} md={8}>
                <BatchJobSettings
                  model={step.allowed_models[0] || (step.input_template as any)?.model || 'gpt-5.4-mini'}
                  maxTokens={(step.input_template as any)?.max_tokens ?? 8192}
                  reasoningEffort={(step.input_template as any)?.reasoning_effort ?? 'low'}
                  onChange={(next) => {
                    const tpl = { ...(step.input_template || {}) };
                    tpl.model = next.model;
                    tpl.max_tokens = next.maxTokens;
                    if (next.reasoningEffort) tpl.reasoning_effort = next.reasoningEffort;
                    else delete tpl.reasoning_effort;
                    updateStep(idx, { allowed_models: [next.model], input_template: tpl });
                  }}
                />
              </Grid>
            )}

            {isNewFunction && (
              <>
                <StepSection title="Instructions" hint="The system prompt: tells the AI what to do in this step." />
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} maxRows={12}
                    value={step.instructions || ''} variant="outlined" size="small"
                    onChange={(e) => updateStep(idx, { instructions: e.target.value })}
                    placeholder="Write detailed instructions for this step..." />
                </Grid>
              </>
            )}

            <StepSection title="Inputs" />
            <Grid item xs={12}>
              <Alert severity="success" variant="outlined" icon={<ArrowIcon />} sx={{ py: 0.25 }}>
                <Typography variant="caption">
                  {idx > 0 ? (
                    <>
                      <strong>Input from:</strong> Step {idx} ({plan?.steps[idx - 1]?.display_name || '?'})
                      {step.map_over_array && step.array_field && (
                        <> &mdash; iterating over <code>{step.array_field}</code> array</>
                      )}
                      {step.input_template && <> &mdash; using custom field mapping</>}
                    </>
                  ) : (
                    <><strong>Input from:</strong> Chain input (the text/data the user provides when running the chain)</>
                  )}
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <StepInputFields
                value={step.input_template}
                fields={(() => {
                  if (!isPlugin) return undefined;
                  const plugin = builderPlugins.find(
                    (p) => p.name === String(step.existing_id || '').replace(/^plugin:/, '')
                  );
                  return plugin ? pluginInputs(plugin) : undefined;
                })()}
                hideKeys={isBatchSubmit ? ['model', 'max_tokens', 'reasoning_effort'] : []}
                onChange={(next) => updateStep(idx, { input_template: Object.keys(next).length ? next : null })}
              />
            </Grid>

            {step.return_object?.fields && step.return_object.fields.length > 0 && (
              <StepSection title="Output fields" />
            )}
            {step.return_object?.fields && step.return_object.fields.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Type</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>Name</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>Description</Typography>
                  </Box>
                  {step.return_object.fields.map((field, fi) => (
                    <Box key={fi} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center', px: 0.5 }}>
                      <Chip label={field.type} size="small" variant="outlined" sx={{ minWidth: 70 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>{field.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>{field.description}</Typography>
                      {field.required && <Chip label="req" size="small" color="error" variant="outlined" />}
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}

            {isAiStep && (
              <>
                <StepSection title="Knowledge" hint="Search Knowledge Hub categories and add the best matches to this step's context." />
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Knowledge categories</InputLabel>
                    <Select
                      multiple
                      value={linkedCategories}
                      label="Knowledge categories"
                      MenuProps={{
                        variant: 'menu',
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: { sx: { maxHeight: 320 } },
                      }}
                      onChange={(e) => {
                        const v = e.target.value;
                        const next = typeof v === 'string' ? v.split(',') : v;
                        updateStep(idx, {
                          knowledge_categories: next,
                          ...(next.length > 0 && step.knowledge_chunks == null ? { knowledge_chunks: 3 } : {}),
                        });
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((id) => <Chip key={id} label={categoryName(id)} size="small" />)}
                        </Box>
                      )}
                    >
                      {knowledgeCategories.length === 0 ? (
                        <MenuItem disabled><em>No categories yet. Create them in Knowledge Hub first.</em></MenuItem>
                      ) : (
                        knowledgeCategories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            <Checkbox size="small" checked={linkedCategories.includes(c.id)} />
                            <ListItemText primary={c.name} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField fullWidth size="small" type="number" label="Snippets"
                    disabled={linkedCategories.length === 0}
                    value={linkedCategories.length > 0 ? (step.knowledge_chunks ?? '') : ''}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateStep(idx, { knowledge_chunks: Number.isFinite(n) ? Math.min(10, Math.max(1, n)) : null });
                    }}
                    onBlur={() => { if (linkedCategories.length > 0 && step.knowledge_chunks == null) updateStep(idx, { knowledge_chunks: 3 }); }}
                    helperText="1–10"
                    inputProps={{ min: 1, max: 10 }} />
                </Grid>
                <Grid item xs={8} md={5}>
                  <TextField fullWidth size="small" label="Search query (optional)"
                    disabled={linkedCategories.length === 0}
                    value={step.knowledge_query || ''}
                    onChange={(e) => updateStep(idx, { knowledge_query: e.target.value || null })}
                    placeholder="e.g. {{node-1.retrieval_query}}"
                    helperText={linkedCategories.length > 0
                      ? 'Supports {{input.field}} and {{node-N.field}}. Blank searches with this step\'s input.'
                      : 'Pick a category first.'} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider />
              <Button
                size="small"
                color="inherit"
                startIcon={<TuneIcon fontSize="small" />}
                endIcon={<ExpandMoreIcon sx={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                onClick={() => setOpenAdvanced((prev) => ({ ...prev, [step.step_number]: !advancedOpen }))}
                sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
              >
                Advanced{isAiStep ? ': array mapping, web search, reliability' : ': array mapping'}
              </Button>
              {!advancedOpen && activeAdvanced.map((a) => (
                <Chip key={a} label={a} size="small" variant="outlined" sx={{ ml: 1, mt: 1 }} />
              ))}
              <Collapse in={advancedOpen} unmountOnExit>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={3}>
                    <Tooltip title="Run this step once per item of an array from the previous step's output." arrow>
                      <FormControlLabel label="Map over array" control={
                        <Switch size="small" checked={step.map_over_array}
                          onChange={(e) => updateStep(idx, { map_over_array: e.target.checked })} />
                      } />
                    </Tooltip>
                  </Grid>
                  {step.map_over_array && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth size="small" label="Array field" value={step.array_field || ''}
                          onChange={(e) => updateStep(idx, { array_field: e.target.value })} placeholder="e.g. results" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={5}>
                        <TextField fullWidth size="small" label="Skip items where this field is empty" value={step.skip_item_when_empty || ''}
                          onChange={(e) => updateStep(idx, { skip_item_when_empty: e.target.value || null })} placeholder="e.g. image_prompt"
                          helperText="Items without it pass through unchanged" />
                      </Grid>
                    </>
                  )}
                  {hasSearch && (
                    <Grid item xs={12}>
                      <FormControlLabel label="Web search (Gemini grounding with Google Search)" control={
                        <Switch size="small" checked={step.enable_google_search || false}
                          onChange={(e) => updateStep(idx, { enable_google_search: e.target.checked })} />
                      } />
                    </Grid>
                  )}
                  {isAiStep && (
                    <>
                      <Grid item xs={12}>
                        <StepReliability functionId={existingFunctionId} functionName={step.display_name} />
                      </Grid>
                      <Grid item xs={12} sm={5} md={4}>
                        <TextField fullWidth size="small" type="number" label="Per-attempt limit (s)"
                          value={step.timeout_seconds ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateStep(idx, { timeout_seconds: v === '' ? null : Number(v) });
                          }}
                          inputProps={{ min: 10, max: 180 }}
                          placeholder="180" />
                      </Grid>
                      <Grid item xs={12} sm={7} md={8}>
                        <Typography variant="caption" color="text.secondary">
                          Longest a single model try may take before it counts as failed and the step's reliability rules
                          (retry or backup model) take over. 10–180s, blank = 180s. Retries, backups and the overall
                          time limit are Reliability settings: set them per step above once the function exists, or for
                          every step at once from the shield in the chain builder toolbar.
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      </Box>
    );
  };

  // ── Render: Phase 2 ───────────────────────────────────────────────────────

  const renderReviewPhase = () => {
    if (!plan) return null;
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.5 }}>
            Chain
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Chain Name" value={chainName}
                onChange={(e) => setChainName(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField fullWidth size="small" label="Chain Description" value={chainDescription}
                onChange={(e) => setChainDescription(e.target.value)} />
            </Grid>
          </Grid>

        {/* Visual flow preview */}
        <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Flow:</Typography>
          {plan.steps.map((step, idx) => {
            const rt = RESPONSE_TYPE_INFO[step.response_type] || RESPONSE_TYPE_INFO.text;
            return (
              <React.Fragment key={step.step_number}>
                <Tooltip title={`${step.description} (${rt.label} output)`}>
                  <Chip
                    icon={rt.icon as any}
                    label={step.display_name}
                    size="small"
                    variant={step.step_type === 'ai_function' ? 'filled' : 'outlined'}
                    color={step.step_type === 'plugin' ? 'secondary' : 'primary'}
                    sx={step.response_type === 'image' ? { bgcolor: '#fff3e0', color: '#e65100', borderColor: '#e65100' } :
                        step.response_type === 'video' ? { bgcolor: '#ffebee', color: '#c62828', borderColor: '#c62828' } : {}}
                  />
                </Tooltip>
                {idx < plan.steps.length - 1 && (
                  <ArrowIcon fontSize="small" color="action" sx={{ mx: 0.25 }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.5 }}>
            Steps · {plan.steps.length}
          </Typography>
          {[
            { label: 'New AI function', color: 'primary.main' },
            { label: 'Plugin', color: 'secondary.main' },
            { label: 'Existing function', color: 'grey.500' },
          ].map((t) => (
            <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color }} />
              <Typography variant="caption" color="text.secondary">{t.label}</Typography>
            </Box>
          ))}
        </Box>

        {plan.steps.map((step, idx) => renderStepCard(step, idx))}

        <Box sx={{ pl: '44px' }}>
          <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={addStep}
            sx={{ borderStyle: 'dashed', color: 'text.secondary', borderColor: 'divider', py: 1, '&:hover': { borderStyle: 'dashed' } }}>
            Add Step
          </Button>
        </Box>
      </Box>
    );
  };

  // ── Render: Phase 3 ───────────────────────────────────────────────────────

  const renderSavePhase = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h6">Chain Created!</Typography>
      <Typography variant="body2" color="text.secondary">
        Your chain and all its functions have been saved. Redirecting to the chain editor...
      </Typography>
    </Box>
  );

  // ── Main Render ───────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={generating || saving || cleaning ? undefined : handleClose} maxWidth={phase === 0 ? 'xl' : 'lg'} fullWidth
      PaperProps={{ sx: { minHeight: '70vh', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>{isEditMode ? 'Edit Chain with AI' : 'Create Chain with AI'}</Typography>
        <IconButton onClick={handleClose} disabled={generating || saving || cleaning} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <Box sx={{ px: 3 }}>
        <Stepper activeStep={phase} alternativeLabel>
          {PHASE_LABELS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {phase === 0 && renderDescribePhase()}
        {phase === 1 && renderReviewPhase()}
        {phase === 2 && renderSavePhase()}
        {saving && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{saveProgress}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        {phase === 0 && (
          <>
            <Tooltip title="Include your custom AI functions in the context sent to the chain planner. Leave off unless your chain needs to reference existing functions." arrow>
              <FormControlLabel
                control={<Switch size="small" checked={includeFunctions} onChange={(_, v) => setIncludeFunctions(v)} disabled={generating || cleaning} />}
                label={<Typography variant="caption" color="text.secondary">Include my functions</Typography>}
                sx={{ ml: 0 }}
              />
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} disabled={generating || cleaning}>Cancel</Button>
              {promptBackup != null && (
                <Button onClick={undoCleanup} disabled={generating || cleaning} startIcon={<UndoIcon />}>
                  Undo cleanup
                </Button>
              )}
              <Tooltip title="Rewrite this draft into a paste-complete builder spec (plugins, inputTemplates, constraints). Does not create the chain yet." arrow>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleCleanupPrompt}
                    disabled={generating || cleaning || description.trim().length < 10}
                    startIcon={cleaning ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
                  >
                    {cleaning ? 'Cleaning…' : 'Clean up prompt'}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={handleGenerate} disabled={generating || cleaning || description.trim().length === 0}
                startIcon={generating ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}>
                {generating ? 'Generating...' : isEditMode ? 'Regenerate Chain Plan' : 'Generate Chain Plan'}
              </Button>
            </Box>
          </>
        )}
        {phase === 1 && (
          <Box sx={{ ml: 'auto' }}>
            <Button onClick={() => setPhase(0)} disabled={saving} sx={{ mr: 1 }}>Back to Description</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || !chainName.trim()}
              startIcon={saving ? <CircularProgress size={18} /> : <CheckIcon />}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {saving ? 'Saving...' : existingChainId ? 'Update Chain & Functions' : 'Create Chain & Functions'}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
